import { Box, Typography } from "@mui/material"
import { ArchiveOutlined, EditOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import React from "react"

import { Button } from "@/components/common/Button"
import type { Comment } from "@/graphql/codegen/graphql"
import { useCommentsActions } from "@/screens/Companies/hooks"
import { formatDate } from "@/utils/date"

interface CommentListItemProps {
  comment: Comment
  isReadOnly?: boolean
  onSuccess: () => void
  onEditComment: (commentId: string) => void
}

const displayDate = (dateString: string | null | undefined) => {
  if (!dateString) return ""
  return `${formatDate(dateString)} at ${new Date(dateString).toLocaleTimeString()}`
}

const CommentListItem: FC<CommentListItemProps> = ({ comment, onSuccess, isReadOnly = false, onEditComment }) => {
  const {
    updatedOn,
    createdOn,
    archivedOn,
    updatedByUser,
    createdByUser,
    archivedByUser,
    value,
    commentId,
    isArchived,
  } = comment

  const { updateComment } = useCommentsActions()

  const toggleArchiveStatus = async (commentId: string, isArchived: boolean) => {
    await updateComment({
      input: {
        commentId,
        isArchived,
      },
    })
    onSuccess()
  }

  const showArchiveInfo = archivedOn && archivedByUser
  return (
    <Box
      className={`group relative flex flex-col gap-y-2 py-3 ${isReadOnly ? "" : "hover:bg-primary-100"} ${isArchived ? "bg-gray-100" : ""}`}
      data-testid={`comment-item-${commentId}`}
    >
      <Box className='group flex w-full justify-between'>
        <Typography variant='caption' className='text-gray-600'>
          Posted: <span data-testid={`comment-created-on-${commentId}`}>{displayDate(createdOn)}</span> By{" "}
          <span data-testid={`comment-created-by-${commentId}`}>{createdByUser?.name}</span>
          {updatedOn && updatedOn !== createdOn && (
            <>
              <br />
              Last Updated: <span data-testid={`comment-updated-on-${commentId}`}>
                {displayDate(updatedOn)}
              </span> By <span data-testid={`comment-updated-by-${commentId}`}>{updatedByUser?.name}</span>
            </>
          )}
        </Typography>
        {!isReadOnly && (
          <Box className='absolute right-2.5 top-0.5 hidden rounded-none group-hover:block'>
            {!isArchived && (
              <Button
                size='small'
                appearance='text'
                startIcon={<EditOutlined />}
                onClick={() => onEditComment(commentId!)}
                className='mr-2 hover:bg-primary-200'
                data-testid={`comment-edit-button-${commentId}`}
              >
                Edit
              </Button>
            )}
            <Button
              size='small'
              appearance='text'
              startIcon={<ArchiveOutlined />}
              onClick={() => toggleArchiveStatus(commentId!, !isArchived)}
              className='hover:bg-primary-200'
              data-testid={`comment-archive-button-${commentId}`}
            >
              {isArchived ? "Unarchive" : "Archive"}
            </Button>
          </Box>
        )}
      </Box>
      <Box>
        <Typography variant='body2' className='text-gray-800' data-testid={`comment-value-${commentId}`}>
          {value}
        </Typography>
      </Box>
      {showArchiveInfo && (
        <Typography variant='caption' className='text-gray-600'>
          {isArchived ? "Archived" : "Unarchived"}:{" "}
          <span data-testid={`comment-archived-on-${commentId}`}>{displayDate(archivedOn)}</span> By{" "}
          <span data-testid={`comment-archived-by-${commentId}`}>{archivedByUser?.name}</span>
        </Typography>
      )}
    </Box>
  )
}

export default CommentListItem
